// REQ-088 (Fase 2 del plan de Reportería) — Tab "Demográfico": distribución de reconocimientos
// por banda generacional (Boomer/Gen X/Millennial/Gen Z/Gen Alpha). La API ya agrupa por banda y
// devuelve el nombre de la banda directamente en `nombre` (no un id) — a diferencia de los demás
// tabs, GetRecognitionGenerationalQueryHandler no tiene el bug de nombres crudos, así que no hace
// falta resolver nada en el front. SIN puntos.
//
// RF-152 — filtros propios BU/Depto/Site/Líderes-Pares, en estado local del tab (independientes
// del FilterBar compartido: la OU/fecha de `params` siguen aplicando igual que antes).
//
// C5/C7 (2026-08-13, BREAKING) — el endpoint dejó de devolver un array plano: ahora envuelve la
// distribución (`bandas`) junto con `ousConCobertura` (ids de OU cuya cobertura de fecha de
// nacimiento alcanzó para aportar al menos una persona con banda resuelta). Se pinta como un
// marcado visual aparte —no forma parte del ranking— porque responde una pregunta distinta:
// "¿el cálculo tuvo datos de ESTA unidad?", no "cuánto reconoció esta banda".
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecognitionGenerational, useSites } from '@/api/recognition';
import type { RecognitionReportParams, GenerationalFilters } from '@/api/recognition';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import { ReportStatus, filtrosActivosDe, soloPersonaDe } from '../reportsShared';
import { DistribucionGeneracional } from './DistribucionGeneracional';

export function GenerationalTab({ params }: Readonly<{ params: RecognitionReportParams }>) {
  const { t } = useTranslation('recognition');
  const [bu, setBu] = useState('');
  const [depto, setDepto] = useState('');
  const [site, setSite] = useState('');
  const [lideresOPares, setLideresOPares] = useState<GenerationalFilters['lideresOPares'] | ''>('');
  const { resolveOuName } = useOuNameResolver();
  // Fase 6 — catálogo real de Sites (antes texto libre, sin validar contra ningún catálogo).
  const sitesQuery = useSites();
  const sites = sitesQuery.data ?? [];
  // La distribución agrupa por banda generacional: con una sola persona seleccionada dejaría de
  // ser una distribución. El Fundamental sí lo acepta el backend (RF-152), así que solo avisa persona.
  const soloPersona = useMemo(() => soloPersonaDe(filtrosActivosDe(params)), [params]);

  const filters: GenerationalFilters = {
    bu: bu || undefined,
    depto: depto || undefined,
    site: site || undefined,
    lideresOPares: lideresOPares || undefined,
  };

  const q = useRecognitionGenerational(params, filters);
  const data = q.data?.bandas ?? [];
  const ousConCobertura = q.data?.ousConCobertura ?? [];
  const isEmpty = !!q.data && data.length === 0;

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-people-group" aria-hidden="true" /> {t('reports.generational.title')}
          </div>
          <div className="au-card-sub">{t('reports.generational.subtitle')}</div>
        </div>
      </div>

      <AvisoFiltroNoAplica filtros={soloPersona} motivo="porBanda" />

      <div className="rr-actions">
        <input
          type="text"
          className="au-date-input"
          placeholder={t('reports.generational.filterBu')}
          value={bu}
          onChange={(e) => setBu(e.target.value)}
          aria-label={t('reports.generational.filterBu')}
        />
        <input
          type="text"
          className="au-date-input"
          placeholder={t('reports.generational.filterDepto')}
          value={depto}
          onChange={(e) => setDepto(e.target.value)}
          aria-label={t('reports.generational.filterDepto')}
        />
        <select
          className="au-cascade-select"
          value={site}
          onChange={(e) => setSite(e.target.value)}
          aria-label={t('reports.generational.filterSite')}
        >
          <option value="">{t('reports.generational.filterSite')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select
          className="au-cascade-select"
          value={lideresOPares}
          onChange={(e) => setLideresOPares(e.target.value as GenerationalFilters['lideresOPares'] | '')}
          aria-label={t('reports.generational.roleAll')}
        >
          <option value="">{t('reports.generational.roleAll')}</option>
          <option value="lideres">{t('reports.generational.roleLeaders')}</option>
          <option value="pares">{t('reports.generational.rolePeers')}</option>
        </select>
      </div>

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {/* T-11 — las tarjetas con color de la maqueta (rep-panel-demografico) SUSTITUYEN a la
            `RankedList` de barras planas.
            
            Se probó apilando las dos y el nombre de cada banda salía DUPLICADO en pantalla. No se
            pierde nada al quitarla: la tarjeta trae el conteo exacto además del porcentaje, el rango
            de años y el color. Y el prototipo es la autoridad de diseño (ver `decisiones.md`), que
            dibuja solo tarjetas. */}
        <DistribucionGeneracional bandas={data} />

        {/* C5/C7 — qué OUs SÍ aportaron cobertura de fecha de nacimiento al cálculo. Ausente de
            esta lista no es "sin actividad": es "sin datos de nacimiento confiables". */}
        {ousConCobertura.length > 0 && (
          <div className="rr-actions--top-gap">
            <div className="rr-block-title--tight">{t('reports.generational.coverageTitle')}</div>
            <div className="rr-coverage-list">
              {ousConCobertura.map((ouId) => (
                <span key={ouId} className="rr-coverage-chip">
                  <i className="fas fa-circle-check" aria-hidden="true" /> {resolveOuName(ouId)}
                </span>
              ))}
            </div>
          </div>
        )}
      </ReportStatus>
    </div>
  );
}
